import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { captureSmartTableEvent } from "./analytics";
import { invalidateVersionHistoryQueries } from "./versionQueryUtils";
import type { AddRowsRequest, GridRow } from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

interface AddRowsResponse {
  success: boolean;
  rows_created: number;
  start_row_index: number;
  cell_count: number;
  row_count: number;
  version: number;
  rows?: GridRow[];
}

export const useAddRows = () => {
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);
  const queryClient = useQueryClient();

  return useMutation<
    AddRowsResponse,
    unknown,
    { tableId: string; sheetId: string } & AddRowsRequest
  >({
    mutationFn: async ({ tableId, sheetId, ...payload }) => {
      const response = await fetch(ENDPOINTS.smart_table_rows(wid, tableId, sheetId), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(payload),
      });
      return (await handleResponse(response)) as AddRowsResponse;
    },
    onSuccess: (data, variables) => {
      invalidateVersionHistoryQueries(queryClient, wid, variables.tableId, variables.sheetId);
      captureSmartTableEvent("Smart Table Rows Added", {
        workspace_id: wid,
        smart_table_id: variables.tableId,
        sheet_id: variables.sheetId,
        rows_added: data.rows_created,
      });
    },
  });
};
